import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { usePageTitle } from '../../context/PageTitleContext';
import { useSidebar } from '../../context/SidebarContext';
import { advancedReportService } from '../../services/api';
import { Card, Table, PageHeader, Pagination } from '../common';
import { default as usePageState } from '../../hooks/usePageState';
import { downloadExcel } from '../../utils/exportUtils';
import { useAuth } from '../../context/AuthContext';
import useDepartment from '../../hooks/useDepartment';

const CategoryReport = () => {
  const { setPageTitle, setPageSubtitle } = usePageTitle();
  const { isCollapsed } = useSidebar();
  const { user, getPagePermissions } = useAuth();
  const dept = useDepartment();
  // Category Type is IT's Software/Hardware split; BioMedical has no such
  // division, so the column is omitted there.
  const showCategoryType = (user?.department_code || 'IT') === 'IT';

  const permissions = getPagePermissions('/reports/category');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ regions: [], categories: [] });

  const [filters, setFilters] = usePageState('categoryReport', {
    start_date: '',
    end_date: '',
    region_code: '',
    category_id: '',
    category_type: '',
    currentPage: 1,
    itemsPerPage: 30,
  }, ['/reports']);

  useEffect(() => {
    setPageTitle('Category Report');
    setPageSubtitle('Category-wise ticket analysis');
    fetchFilterOptions();
  }, [setPageTitle, setPageSubtitle]);

  const fetchFilterOptions = async () => {
    try {
      const response = await advancedReportService.getFilters();
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.start_date) params.filter = 'custom';
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.region_code) params.region_code = filters.region_code;
      if (filters.category_id) params.category_id = filters.category_id;
      if (filters.category_type) params.category_type = filters.category_type;
      const response = await advancedReportService.getCategoryReport(params);
      setData(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching category report:', error);
    } finally {
      setLoading(false);
    }
  }, [filters.start_date, filters.end_date, filters.region_code, filters.category_id, filters.category_type]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatHours = (hours) => {
    if (!hours || hours === 0) return '-';
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    return `${hours}h`;
  };

  // Pagination
  const totalItems = data.length;
  const totalPages = Math.ceil(totalItems / filters.itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (filters.currentPage - 1) * filters.itemsPerPage;
    return data.slice(start, start + filters.itemsPerPage);
  }, [data, filters.currentPage, filters.itemsPerPage]);

  const handleDownload = () => {
    downloadExcel(data, columns, 'category_report');
  };

  const columns = [
    {
      header: 'S.No',
      accessor: 'serial',
      width: '60px',
      sticky: true,
      render: (_, __, index) => (filters.currentPage - 1) * filters.itemsPerPage + index + 1
    },
    {
      header: 'Category',
      accessor: 'category',
      sticky: true,
      render: (value) => <span className="font-semibold text-gray-900">{value}</span>
    },
    ...(showCategoryType ? [{
      header: 'Category Type',
      accessor: 'categoryType',
      align: 'center',
      render: (value) => (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
          value === 'Software'
            ? 'bg-blue-100 text-blue-700'
            : value === 'Hardware'
            ? 'bg-purple-100 text-purple-700'
            : 'bg-gray-100 text-gray-700'
        }`}>
          {value || 'N/A'}
        </span>
      )
    }] : []),
    {
      header: 'Total Tickets',
      accessor: 'totalTickets',
      align: 'center',
      render: (value) => <span className="font-semibold">{value}</span>
    },
    {
      header: 'Active',
      accessor: 'active',
      align: 'center',
      render: (value) => (
        <span className={`font-semibold ${value > 0 ? 'text-blue-600' : 'text-gray-500'}`}>{value}</span>
      )
    },
    {
      header: 'Resolved',
      accessor: 'resolved',
      align: 'center',
      render: (value) => <span className="font-semibold text-green-600">{value}</span>
    },
    {
      header: 'Reopened',
      accessor: 'reopened',
      align: 'center',
      render: (value) => (
        <span className={`font-semibold ${value > 0 ? 'text-orange-600' : 'text-gray-500'}`}>{value}</span>
      )
    },
    {
      header: 'Avg Resolution Time',
      accessor: 'avgResolutionTime',
      align: 'center',
      render: (value) => <span className="text-gray-700">{formatHours(value)}</span>
    },
    {
      header: 'Resolution SLA %',
      accessor: 'resolutionSlaPct',
      align: 'center',
      render: (value) => (
        <div className="flex items-center justify-center gap-2">
          <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[60px]">
            <div
              className="h-2 rounded-full transition-all duration-500"
              style={{
                width: `${Math.min(value, 100)}%`,
                backgroundColor: value >= 80 ? '#10b981' : value >= 50 ? '#f59e0b' : '#ef4444'
              }}
            />
          </div>
          <span className="font-medium text-sm" style={{ color: value >= 80 ? '#059669' : value >= 50 ? '#d97706' : '#dc2626' }}>
            {value}%
          </span>
        </div>
      )
    },
    {
      header: 'Reopen %',
      accessor: 'reopenPct',
      align: 'center',
      render: (value) => (
        <span className={`font-medium ${value > 10 ? 'text-red-600' : value > 5 ? 'text-yellow-600' : 'text-green-600'}`}>
          {value}%
        </span>
      )
    },
    {
      header: 'Escalation %',
      accessor: 'escalationPct',
      align: 'center',
      render: (value) => (
        <span className={`font-medium ${value > 10 ? 'text-red-600' : value > 5 ? 'text-yellow-600' : 'text-green-600'}`}>
          {value}%
        </span>
      )
    },
    {
      header: 'Trend vs Prev',
      accessor: 'trendPercentage',
      align: 'center',
      render: (value) => {
        const isUp = value > 0;
        const isDown = value < 0;
        const color = isUp ? 'text-red-600' : isDown ? 'text-green-600' : 'text-gray-500';
        const arrow = isUp ? '\u25B2' : isDown ? '\u25BC' : '\u2013';
        return (
          <span className={`font-semibold ${color}`}>
            {arrow} {Math.abs(value)}%
          </span>
        );
      }
    },
  ];

  return (
    <div className="admin-panel h-full">
      <Card compact={isCollapsed} allowOverflow={true} noBottomPadding={true} className="h-full flex flex-col">
        <PageHeader
          filters={{
            dateRange: {
              startDate: filters.start_date,
              endDate: filters.end_date,
              onStartDateChange: (e) => setFilters({ ...filters, start_date: e.target.value, currentPage: 1 }),
              onEndDateChange: (e) => setFilters({ ...filters, end_date: e.target.value, currentPage: 1 }),
            },
            dropdowns: [
              {
                value: filters.region_code,
                onChange: (e) => setFilters({ ...filters, region_code: e.target.value, currentPage: 1 }),
                options: [{ value: '', label: `All ${dept.regions}` }, ...(filterOptions.regions || [])],
                placeholder: `All ${dept.regions}`,
                searchable: true,
                width: '180px'
              },
              {
                value: filters.category_id,
                onChange: (e) => setFilters({ ...filters, category_id: e.target.value, currentPage: 1 }),
                options: [{ value: '', label: 'All Categories' }, ...(filterOptions.categories || [])],
                placeholder: 'All Categories',
                searchable: true,
                width: '180px'
              },
              // Category Type is IT-only, like the column it filters
              ...(showCategoryType ? [{
                value: filters.category_type,
                onChange: (e) => setFilters({ ...filters, category_type: e.target.value, currentPage: 1 }),
                options: [
                  { value: '', label: 'All Types' },
                  { value: 'Software', label: 'Software' },
                  { value: 'Hardware', label: 'Hardware' }
                ],
                placeholder: 'All Types',
                searchable: false,
                width: '140px'
              }] : [])
            ]
          }}
          downloadButton={permissions.can_download ? {
            label: 'Download',
            onClick: handleDownload,
            variant: 'outline',
            className: 'border-primary-600 text-primary-600 hover:bg-primary-50 focus:ring-primary-500',
            disabled: loading || data.length === 0
          } : null}
        />
        <div className="mb-2 px-1">
          <div className="bg-blue-50 border-l-4 border-blue-400 p-2 rounded">
            <p className="text-xs text-blue-800">
              <strong>Resolution SLA %</strong> = Tickets resolved within SLA / Total tickets.
              <strong> Trend</strong> = Change in total tickets vs previous equivalent period.
              <span className="text-red-600 ml-1">{'\u25B2'} Increase</span> |
              <span className="text-green-600 ml-1">{'\u25BC'} Decrease</span>
            </p>
          </div>
        </div>
        <Table
          columns={columns}
          data={paginatedData}
          loading={loading}
          emptyMessage="No category data available for the selected filters"
        />
        <Pagination
          currentPage={filters.currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={filters.itemsPerPage}
          onPageChange={(page) => setFilters({ ...filters, currentPage: page })}
          onItemsPerPageChange={(count) => setFilters({ ...filters, itemsPerPage: count, currentPage: 1 })}
        />
      </Card>
    </div>
  );
};

export default CategoryReport;
